import React, { useState, useEffect } from 'react';
import {
  ShieldCheck, AlertCircle, Info, ChevronRight, Check, X,
  Key, Settings, Globe, Database, Cpu, Send, MessageSquare, Phone,
  Activity, Zap, Lock, Eye, EyeOff, RefreshCcw
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription, DialogFooter
} from "../ui/dialog";
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import API from '../api/api01';
import { toast } from 'sonner';
import { cn } from '../ui/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface GatewayConfigModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  providerId?: string;
  providerSlug?: string;
  configId?: string;
}

export default function GatewayConfigModal({ open, onOpenChange, onSuccess, providerId, providerSlug, configId }: GatewayConfigModalProps) {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [schemas, setSchemas] = useState<any[]>([]);
  const [currentSchema, setCurrentSchema] = useState<any>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [clients, setClients] = useState<any[]>([]);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [formData, setFormData] = useState<any>({
    channel: 'sms',
    priority: 0,
    is_active: true,
    tenant_id: null,
    credentials: {}
  });

  useEffect(() => {
    if (open) {
      fetchSchemas();
      
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          const isSA = user.role?.toLowerCase() === 'superadmin' || user.is_superuser;
          setIsSuperadmin(isSA);
          if (isSA) {
            fetchClients();
          }
        } catch (e) {}
      }

      if (configId) {
        fetchConfig();
      } else {
        setFormData({
          channel: 'sms',
          priority: 0,
          is_active: true,
          tenant_id: null,
          credentials: {}
        });
      }
    }
  }, [open, configId]);

  useEffect(() => {
    if (schemas.length > 0 && providerSlug) {
      const schema = schemas.find(s => s.slug === providerSlug);
      setCurrentSchema(schema || null);
    }
  }, [schemas, providerSlug]);

  const fetchSchemas = async () => {
    try {
      const res = await API.get('/gateway/providers/schemas/');
      setSchemas(res.data);
    } catch (err) {
      console.error("Failed to fetch schemas:", err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await API.get('/superadmin/clients');
      setClients(res.data || []);
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const res = await API.get(`/gateway/configs/${configId}/`);
      setFormData({
        channel: res.data.channel,
        priority: res.data.priority,
        is_active: res.data.is_active,
        tenant_id: res.data.tenant_id || null,
        credentials: res.data.credentials || {}
      });
    } catch (err) {
      toast.error("Failed to sync configuration");
    } finally {
      setLoading(false);
    }
  };

  const handleCredentialChange = (name: string, value: string) => {
    setFormData((prev: any) => ({
      ...prev,
      credentials: {
        ...prev.credentials,
        [name]: value
      }
    }));
  };

  const handleTestConnection = async () => {
    // Basic field validation before "testing"
    const requiredFields = filteredFields.filter((f: any) => f.required);
    const missingFields = requiredFields.filter((f: any) => !formData.credentials[f.name]);

    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields.map((f: any) => f.label).join(', ')}`);
      return;
    }

    setTesting(true);
    // Simulate API validation handshake
    await new Promise(resolve => setTimeout(resolve, 1500));
    setTesting(false);
    toast.success("Handshake successful! Credentials format validated.");
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = {
        ...formData,
        provider: providerId
      };

      if (configId) {
        await API.put(`/gateway/configs/${configId}/`, payload);
        toast.success("Channel parameters updated");
      } else {
        await API.post('/gateway/configs/', payload);
        toast.success("Gateway channel successfully linked");
      }
      onSuccess();
      onOpenChange(false);
    } catch (err: any) {
      const errorMsg = err.response?.data?.non_field_errors?.[0] || "Gateway sync failed";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const toggleSecret = (name: string) => {
    setShowSecrets(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'sms': return <Send className="h-4 w-4 text-blue-500" />;
      case 'whatsapp': return <MessageSquare className="h-4 w-4 text-emerald-500" />;
      case 'voice': return <Phone className="h-4 w-4 text-orange-500" />;
      default: return <Cpu className="h-4 w-4" />;
    }
  };

  const filteredFields = currentSchema?.schema?.filter((field: any) => 
    !field.channels || field.channels.includes(formData.channel)
  ) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] w-[95vw] p-0 bg-white dark:bg-slate-900 border-none shadow-[0_32px_64px_-12px_rgba(0,0,0,0.3)] rounded-[2rem] max-h-[90vh] flex flex-col overflow-hidden">
        {/* Close Button */}
        <button 
          onClick={() => onOpenChange(false)}
          className="absolute top-6 right-6 z-50 p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-500 transition-all hover:rotate-90"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Animated Gradient Border Top */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-emerald-600 animate-gradient-x" />
        
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-64 h-64 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

        {/* Header - Fixed */}
        <div className="p-8 pb-4 relative z-10 shrink-0">
          <DialogHeader className="space-y-1">
            <div className="flex items-center justify-between mb-4">
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 text-[10px] font-black uppercase tracking-[0.2em] border border-slate-200 dark:border-slate-800"
              >
                <Zap size={12} className="text-yellow-500 fill-yellow-500" />
                Intelligent Gateway Proxy
              </motion.div>
              <Badge variant="outline" className="border-emerald-500/20 text-emerald-500 bg-emerald-500/5 text-[9px] font-black px-3 py-1 uppercase tracking-widest rounded-lg">
                Secure Layer V2
              </Badge>
            </div>
            <DialogTitle className="text-3xl font-black tracking-tighter leading-tight bg-clip-text text-transparent bg-gradient-to-br from-slate-900 to-slate-500 dark:from-white dark:to-slate-500">
              {configId ? 'Refine Channel' : `Integrate ${providerSlug?.toUpperCase()}`}
            </DialogTitle>
            <DialogDescription className="text-slate-500 dark:text-slate-400 text-sm font-medium">
              Establish a secure bridge between your business logic and <span className="text-primary font-bold">{providerSlug?.toUpperCase()}</span> APIs.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Scrollable Middle Content - Flexible */}
        <div className="p-8 pt-4 space-y-8 overflow-y-auto scrollbar-hide relative z-10 flex-1">
          {/* Core Configuration Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2">
                <Globe size={12} />
                Protocol Type
              </Label>
              <Select 
                value={formData.channel} 
                onValueChange={(v) => setFormData({...formData, channel: v})}
              >
                <SelectTrigger className="rounded-2xl h-12 bg-slate-50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 hover:border-primary/50 transition-all shadow-sm">
                  <div className="flex items-center gap-3">
                    {getChannelIcon(formData.channel)}
                    <SelectValue placeholder="Select Protocol" />
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-xl min-w-[240px]">
                  <SelectItem value="sms" className="rounded-xl focus:bg-blue-500/10 focus:text-blue-600 transition-colors py-3">
                    <div className="flex items-center gap-2 font-bold truncate">
                       <Send className="h-4 w-4 shrink-0" /> <span className="truncate">SMS Gateway</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="whatsapp" className="rounded-xl focus:bg-emerald-500/10 focus:text-emerald-600 transition-colors py-3">
                    <div className="flex items-center gap-2 font-bold truncate">
                       <MessageSquare className="h-4 w-4 shrink-0" /> <span className="truncate">WhatsApp Business</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="voice" className="rounded-xl focus:bg-orange-500/10 focus:text-orange-600 transition-colors py-3">
                    <div className="flex items-center gap-2 font-bold truncate">
                       <Phone className="h-4 w-4 shrink-0" /> <span className="truncate">Voice / OBD</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2">
                <Activity size={12} />
                Routing Priority
              </Label>
              <div className="relative group">
                <Input
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value)})}
                  className="rounded-2xl h-12 bg-slate-50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 pl-12 focus:ring-primary/50 transition-all font-bold"
                />
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-hover:text-primary transition-colors">
                  <RefreshCcw size={16} />
                </div>
              </div>
            </div>

            {isSuperadmin && (
              <div className="space-y-3 md:col-span-2">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-2">
                  <ShieldCheck size={12} className="text-indigo-500" />
                  Multi-Tenant Assignment (Client Isolation)
                </Label>
                <Select
                  value={formData.tenant_id || "global"}
                  onValueChange={(v) => setFormData({...formData, tenant_id: v === "global" ? null : v})}
                >
                  <SelectTrigger className="rounded-2xl h-12 bg-slate-50 dark:bg-slate-950/50 border-slate-200 dark:border-slate-800 hover:border-primary/50 transition-all shadow-sm">
                    <div className="flex items-center gap-3">
                      <span className="font-bold truncate">
                        {formData.tenant_id ? clients.find(c => c.id === formData.tenant_id)?.name || "Client Config" : "Global System (All Clients)"}
                      </span>
                    </div>
                  </SelectTrigger>
                  <SelectContent className="rounded-2xl border-slate-200 dark:border-slate-800 shadow-xl min-w-[240px]">
                    <SelectItem value="global" className="rounded-xl focus:bg-indigo-500/10 focus:text-indigo-600 transition-colors py-3">
                      <div className="flex items-center gap-2 font-bold truncate">
                        <Globe className="h-4 w-4 text-slate-400 shrink-0" />
                        <span className="truncate">Global System-wide Configuration</span>
                      </div>
                    </SelectItem>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id} className="rounded-xl focus:bg-primary/10 focus:text-primary transition-colors py-3">
                        <div className="flex items-center gap-2 font-bold truncate">
                          <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shrink-0" />
                          <span className="truncate">{c.name} ({c.login_id})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Dynamic Logic Section */}
          <AnimatePresence mode="wait">
            <motion.div
              key={formData.channel}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="space-y-6"
            >
              {filteredFields.length > 0 ? (
                <div className="space-y-5">
                  <div className="flex items-center justify-between border-b border-border/50 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                        <Lock size={18} />
                      </div>
                      <div>
                        <h4 className="text-sm font-black tracking-tight text-slate-900 dark:text-white">Access Credentials</h4>
                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Required for Handshake</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleTestConnection} className="h-8 rounded-lg text-[10px] font-black uppercase tracking-widest gap-2 hover:bg-primary/5 text-primary border border-primary/20">
                      {testing ? <RefreshCcw size={12} className="animate-spin" /> : <Zap size={12} />}
                      Test Link
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 gap-5">
                    {filteredFields.map((field: any) => (
                      <div key={field.name} className="space-y-2 group">
                        <Label className="text-[10px] font-black uppercase tracking-wider text-slate-500 ml-1 flex items-center justify-between group-hover:text-primary transition-colors">
                          {field.label}
                          {field.required && <Badge variant="outline" className="text-[7px] border-red-500/20 text-red-500 px-1.5 h-3.5">MANDATORY</Badge>}
                        </Label>
                        <div className="relative">
                          <Input
                            type={field.type === 'password' && !showSecrets[field.name] ? 'password' : 'text'}
                            value={formData.credentials[field.name] || ''}
                            onChange={(e) => handleCredentialChange(field.name, e.target.value)}
                            placeholder={field.placeholder || `Enter ${field.label}...`}
                            className="rounded-2xl h-13 bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 pr-12 focus:ring-2 focus:ring-primary/20 transition-all font-medium text-sm"
                          />
                          {field.type === 'password' && (
                            <button
                              type="button"
                              onClick={() => toggleSecret(field.name)}
                              className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-primary transition-colors"
                            >
                              {showSecrets[field.name] ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <motion.div 
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  className="p-12 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-[2.5rem] bg-slate-50/50 dark:bg-slate-950/30 backdrop-blur-sm group hover:border-primary/50 transition-colors"
                >
                  <div className="h-20 w-20 rounded-3xl bg-slate-100 dark:bg-slate-900 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-inner">
                    <Database className="h-10 w-10 text-slate-300 group-hover:text-primary transition-colors" />
                  </div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">Autonomous Configuration</h3>
                  <p className="text-xs text-slate-500 font-medium mt-2 max-w-[240px] mx-auto">
                    This protocol for <span className="text-primary font-bold">{providerSlug?.toUpperCase()}</span> uses global system parameters. No manual credentials required.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </AnimatePresence>

        
        </div>

        {/* Footer - Fixed */}
        <DialogFooter className="p-8 border-t border-slate-100 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-2xl flex flex-row items-center justify-between gap-4 shrink-0">
          <Button 
            variant="ghost" 
            onClick={() => onOpenChange(false)} 
            className="rounded-2xl h-14 px-8 font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex-1 sm:flex-none"
          >
            Discard Changes
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="rounded-2xl h-14 px-12 font-black shadow-[0_20px_40px_-12px_rgba(var(--primary),0.4)] bg-primary hover:bg-primary/90 text-white min-w-[200px] gap-2 flex-1 sm:flex-none"
          >
            {loading ? <RefreshCcw size={18} className="animate-spin" /> : <ChevronRight size={18} />}
            {configId ? 'Commit Updates' : 'Initialize Gateway'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Badge = ({ children, variant = 'default', className }: any) => {
  const variants = {
    default: 'bg-primary text-white',
    outline: 'border border-border',
    secondary: 'bg-secondary text-secondary-foreground'
  };
  return (
    <span className={cn("px-2 py-0.5 rounded text-xs font-medium", (variants as any)[variant], className)}>
      {children}
    </span>
  );
};
